import React, { useState } from 'react';
import TextField from "../FieldType/TextField/TextField";
import MultiChoiceField from "../FieldType/MultiChoiceField/MultiChoiceField";
import CategoryComponent from "../FieldType/Category/CategoryComponent";
import IntegerField from "../FieldType/Integer/IntegerField";
import DecimalField from "../FieldType/Decimal/DecimalField";
import FractionField from "../FieldType/Fraction/FractionField";
import BooleanField from "../FieldType/Boolean/BooleanField";
import RangeField from "../FieldType/Range/RangeField";
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import TrixTextEditor from 'Pages/GMS/ISR/Components/TrixTextEditor';
import { SubModelIcon } from 'assests/GmsIcons';
import YourBuildWidget from '../Widgets/YourBuildWidget/YourBuildWidget';
import ButtonWidget from '../Widgets/ButtonWidget/ButtonWidget';
import { Submodel, ContainerItem, UsedComponent } from '../../../types/canvas.types';
import { useEditor } from '../../../contexts/EditorContext';
import { useModule } from '../../../contexts/ModuleContext';
import { useUIModelApi } from '../../../contexts/UIModelApiContext';
import { generateInstanceId } from '../../../utils/IdGenerator';
import AppSettings from 'Settings/AppSettings';

type ContainerContentProps = {
    id?: string;
    componentId: string;
    usedComponents?: UsedComponent[];
    items?: ContainerItem[];
    containerConfig?: {
        width: string;
        height: string;
        alignment: 'left' | 'center' | 'right';
        layout: 'vertical' | 'horizontal';
        showTitle: boolean;
    };
    onFieldSelect?: (fieldId: string, fieldType: string, parentId?: string) => void;
    onSubmodelSelect?: (submodelId: string) => void;
    onWidgetSelect?: (widgetId: string, widgetType: string, configuration?: any) => void;
    selectedFieldId?: string | null;
    selectedSubmodelId?: string | null;
    selectedWidgetId?: string | null;
    submodel?: Submodel;
    onContentUpdate?: (widgetId: string, content: string) => void;
    isNested?: boolean;
}

const ContainerContent = ({ id, componentId, usedComponents = [],items = [], containerConfig,onFieldSelect,onSubmodelSelect,onWidgetSelect,selectedFieldId,selectedSubmodelId,selectedWidgetId,submodel, onContentUpdate, isNested = false }: ContainerContentProps): React.ReactElement => {
    const baseId = id || 'container-content';
    const { getComponentDefinition } = useComponentRegistry();
    const { editorState, selectedFieldId: globalSelectedFieldId, selectedWidgetId: globalSelectedWidgetId } = useEditor();
    const { industryId } = useModule();
    const { configureId } = useUIModelApi();
    
    // Always use global state for selections to ensure consistency
    const effectiveSelectedFieldId = globalSelectedFieldId;
    const effectiveSelectedWidgetId = globalSelectedWidgetId;

    const [contentStates, setContentStates] = useState<{ [key: string]: { content: string; hasUnsavedChanges: boolean } }>({});

    const layout = containerConfig?.layout || 'vertical';

    const getLayoutClasses = () => {
        if (layout === 'horizontal') {
            return 'tw-flex tw-flex-row tw-flex-wrap tw-gap-2 tw-w-full';
        }
        return 'tw-flex tw-flex-col tw-gap-1 tw-w-full';
    };
    
    const submodelInfo = submodel ? getComponentDefinition(submodel.id) : null;

    const hasContent = () => {
        if (submodel) return true;
        if (items && items.length > 0) return true;
        if (usedComponents && usedComponents.length > 0) return true;
        if (componentId && componentId !== 'empty') return true;
        return false;
    };
    
    if (!hasContent()) {
        return (
            <div id={`${baseId}-empty-state`} className="tw-flex tw-items-center tw-justify-center tw-h-16 tw-bg-gray-50 tw-rounded-md tw-mt-2 tw-border tw-border-dashed tw-border-gray-300">
                <span id={`${baseId}-empty-message`} className="tw-text-sm tw-text-gray-500">Select a component to add</span>
            </div>
        );
    }
    
    const handleFieldClick = (e: React.MouseEvent, item: any) => {
        e.stopPropagation();
        
        if (onFieldSelect) {
            const itemType = item.type || 'field';
            const fieldType = item.fieldType || itemType;
            
            onFieldSelect(
                item.id || item.originalComponentId, 
                fieldType,
                item.parentId
            );
        }
    };

    const handleWidgetClick = (e: React.MouseEvent, widget: any) => {
        e.stopPropagation();
        
        if (onWidgetSelect) {
            onWidgetSelect(widget.id, widget.widgetType, widget.configuration || widget.config);
        }
    };

    const handleSubmodelClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        if (onSubmodelSelect && submodel) {
            onSubmodelSelect(submodel.id);
        }
    };

    const isSubmodelHighlighted = () => {
        if (!submodel) return false;
        
        if (selectedSubmodelId === submodel.id) return 'primary';
        
        if (editorState.selectionPath.includes(submodel.id)) {
            const pathIndex = editorState.selectionPath.indexOf(submodel.id);
            const pathLength = editorState.selectionPath.length;
            const distanceFromEnd = pathLength - pathIndex - 1;
            
            if (distanceFromEnd === 0) return 'primary';
            if (distanceFromEnd === 1) return 'secondary';
            return 'tertiary';
        }
        
        return false;
    };

    const submodelHighlightLevel = isSubmodelHighlighted();

    const buildCategoryProps = (config: any) => {
    return ({
        categoryId: config.categoryId ?? '',
        industryId,
        direction: config.direction || "horizontal",
        title: config.titleText || config.title || 'Select Category',
        description: config.descriptionText || 'Select from available options',
        showInStockOnly: config.showInStock || false,
        label: config.label,
        showTitle: config.showTitle !== undefined ? config.showTitle : true,
        titleText: config.titleText,
        showCategoryDescription: config.showCategoryDescription !== undefined ? config.showCategoryDescription : true,
        descriptionText: config.descriptionText,
        showThumbnail: config.showThumbnail !== undefined ? config.showThumbnail : true,
        imageDisplay: config.imageDisplay || 'Fit',
        categoryImageUrl: config.categoryImageUrl || '',
        showSortByAttributes: config.showSortByAttributes !== undefined ? config.showSortByAttributes : true,
        sortConfig: config.sortConfig,
        showInStock: config.showInStock !== undefined ? config.showInStock : true,
        inStockText: config.inStockText || 'Show In Stock only',
        selectByDefault: config.selectByDefault || false,
        hideIfNoProducts: config.hideIfNoProducts || false,
        style: config.style || 'Grid',
        productsPerRow: config.productsPerRow || '4',
        productsLoading: config.productsLoading || 'Infinite Scroll',
        scrollBarAfterRows: config.scrollBarAfterRows || '4',
        showButtonAfterProducts: config.showButtonAfterProducts || '20',
        scrollBarAfterProducts: config.scrollBarAfterProducts || '20',
        showModelNumber: config.showModelNumber !== undefined ? config.showModelNumber : true,
        showProductDescription: config.showProductDescription !== undefined ? config.showProductDescription : true,
        descriptionStyle: config.descriptionStyle || 'Partly',
        showPrice: config.showPrice !== undefined ? config.showPrice : true,
        showAvailabilityStatus: config.showAvailabilityStatus !== undefined ? config.showAvailabilityStatus : true,
        inStockLabel: config.inStockLabel || 'In Stock',
        backOrderLabel: config.backOrderLabel || 'Enter Label',
        showVariants: config.showVariants || false,
        showNumberOfVariants: config.showNumberOfVariants || false,
        showCategory: config.showCategory || false,
        showManufacturer: config.showManufacturer || false,
        productDetailsTextAlignment: config.productDetailsTextAlignment || 'Left',
        showButtonText: config.showButtonText !== undefined ? config.showButtonText : true,
        buttonLabel: config.buttonLabel || 'Add to Cart',
        showButtonIcon: config.showButtonIcon || false,
        buttonIconUrl: config.buttonIconUrl || '',
        buttonPlacement: config.buttonPlacement || 'Left',
        actualCategoryId: config.actualCategoryId
    });
};

    const handleContentChange = (widgetId: string, newContent: string) => {
        setContentStates(prev => ({
            ...prev,
            [widgetId]: {
                content: newContent,
                hasUnsavedChanges: true
            }
        }));
    };

    const handleDoneClick = (widgetId: string) => {
        const state = contentStates[widgetId];
        if (state && onContentUpdate) {
            onContentUpdate(widgetId, state.content);
            setContentStates(prev => ({
                ...prev,
                [widgetId]: {
                    ...prev[widgetId],
                    hasUnsavedChanges: false
                }
            }));
        }
    };

    const renderContentBlock = (widgetItem?: any) => {
        const instanceId = widgetItem?.id || generateInstanceId('content-block-widget');
        const currentState = contentStates[instanceId] || {
            content: widgetItem?.content || '',
            hasUnsavedChanges: false
        };
        const blockId = `${baseId}-content-block-${instanceId}`;

        return (
            <div id={blockId} className="tw-text-sm tw-text-[#6B788C] tw-mt-2 tw-p-4 tw-bg-gray-50 tw-rounded-md">
                <TrixTextEditor
                    id={`${blockId}-editor`}
                    updateInfo={(newContent) => {
                        handleContentChange(instanceId, newContent);
                    }}
                    value={currentState.content}
                    selectedItemId={instanceId}
                    attachmentUrl={`${AppSettings.GMS_BACKEND_API_URL}/isr/models/${configureId ?? ''}/image/upload`}
                />
                {currentState.hasUnsavedChanges && (
                    <div id={`${blockId}-actions`} className="tw-mt-3 tw-flex tw-justify-end">
                        <button
                            id={`${blockId}-done-btn`}
                            data-testid={`${blockId}-done-btn`}
                            onClick={() => handleDoneClick(instanceId)}
                            className="tw-px-4 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-rounded tw-hover:tw-bg-blue-700 tw-transition-colors"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderDivider = () => {
        return (
            <div id={`${baseId}-divider`} className="tw-w-full tw-h-px tw-bg-[#DADBDD] tw-my-2"/>
        );
    };

    const renderWidget = (widget: any) => {
        switch (widget.widgetType) {
            case 'divider':
                return renderDivider();
            case 'contentBlock':
                return renderContentBlock(widget);
            case 'yourBuild':
                return (
                    <YourBuildWidget
                        id={`${baseId}-your-build-${widget.id}`}
                        config={widget.configuration || widget.config}
                        widgetId={widget.id}
                        isSelected={effectiveSelectedWidgetId === widget.id}
                        onContentUpdate={onContentUpdate}
                    />
                );
            case 'button':
                return (
                    <ButtonWidget
                        id={`${baseId}-button-${widget.id}`}
                        config={widget.configuration || widget.config}
                        widgetId={widget.id}
                        isSelected={effectiveSelectedWidgetId === widget.id}
                    />
                );
            case 'visualizer':
                return (
                    <div id={`${baseId}-visualizer`} className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-20 tw-bg-gray-50 tw-rounded-md tw-mt-2 tw-p-4">
                        <span id={`${baseId}-visualizer-title`} className="tw-text-base tw-font-medium tw-text-[#6B788C] tw-mb-1">Visualizer</span>
                        <p id={`${baseId}-visualizer-description`} className="tw-text-xs tw-text-[#6B788C] tw-text-center">This widget provides a visual representation of the build</p>
                    </div>
                );
            case 'summary':
                return (
                    <div id={`${baseId}-summary`} className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-20 tw-bg-gray-50 tw-rounded-md tw-mt-2 tw-p-4">
                        <span id={`${baseId}-summary-title`} className="tw-text-base tw-font-medium tw-text-[#6B788C] tw-mb-1">Summary</span>
                        <p id={`${baseId}-summary-description`} className="tw-text-xs tw-text-[#6B788C] tw-text-center">This widget displays a summary of all selections</p>
                    </div>
                );
            default:
                return (
                    <div id={`${baseId}-unknown-widget`} className="tw-text-sm tw-text-gray-400 tw-italic tw-mt-2 tw-p-2 tw-border tw-border-dashed tw-border-gray-300 tw-rounded tw-bg-gray-50">
                        Widget: {widget.widgetType || 'Unknown'}
                    </div>
                );
        }
    };

    const renderFieldItem = (field: any, isSelected: boolean = false) => {
        const fieldName = field.name || field.configuration?.fieldName || 'Field';
        const fieldConfig = field.configuration || {};
        
        if (field.fieldType === 'category' || field.type === 'category') {
            return (
                <CategoryComponent 
                    key={`field-category-${field.id}-${JSON.stringify(fieldConfig).substring(0, 50)}`}
                    {...buildCategoryProps({
                        ...fieldConfig,
                        id: fieldConfig.id || field.id,
                        categoryId: fieldConfig.categoryId || field.dataConfiguration?.categoryId || field.id,
                        isEmpty: fieldConfig.isEmpty ?? field.dataConfiguration?.isEmpty ?? false,
                        actualCategoryId: fieldConfig.actualCategoryId || fieldConfig.categoryId || field.id
                    })}
                    isSelected={isSelected}
                />
            );
        }
        
        if (field.fieldType === 'connector' || field.type === 'connector') {
            const connectorId = `${baseId}-connector-${field.id}`;
            return (
                <div key={field.id} id={connectorId} className={`tw-p-3 tw-border tw-rounded ${isSelected ? 'tw-bg-[#DADBDD]' : 'tw-bg-gray-50'}`}>
                    <div id={`${connectorId}-content`} className="tw-font-medium tw-flex tw-items-center">
                        <svg id={`${connectorId}-icon`} className="tw-w-4 tw-h-4 tw-mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span id={`${connectorId}-label`}>
                            {fieldConfig.showTitle !== false ? (
                                fieldConfig.displayName || fieldName
                            ) : null}
                        </span>
                    </div>
                </div>
            );
        }
        
        const commonFieldProps = {
            key: field.id,
            id: field.id,
            label: fieldConfig.label || fieldName,
            showTitle: fieldConfig.showTitle !== false,
            displayName: fieldConfig.displayName,
            isSelected: isSelected
        };

        switch (field.fieldType) {
            case 'text':
                return (
                    <TextField 
                        {...commonFieldProps}
                        title={fieldName}
                        exclusionType={fieldConfig.exclusionType || 'hide'}
                    />
                );

            case 'integer':
                return (
                    <IntegerField 
                        {...commonFieldProps}
                        title={fieldName}
                    />
                );

            case 'decimal':
                return (
                    <DecimalField 
                        {...commonFieldProps}
                        title={fieldName}
                    />
                );

            case 'fraction':
                return (
                    <FractionField 
                        {...commonFieldProps}
                        title={fieldName}
                    />
                );

            case 'boolean':
                return (
                    <BooleanField 
                        {...commonFieldProps}
                    />
                );

            case 'range':
                return (
                    <RangeField 
                        {...commonFieldProps}
                        title={fieldName}
                        exclusionType={fieldConfig.exclusionType || 'hide'}
                        min={fieldConfig.min || 0}
                        max={fieldConfig.max || 100}
                        step={fieldConfig.step || 1}
                    />
                );
                
            case 'multichoice':
                const choices = field.dataConfiguration?.choices || [];
                const multiChoiceConfig = field.configuration || {};

                return (
                    <MultiChoiceField
                        {...commonFieldProps}
                        choices={choices}
                        title={fieldName}
                        style={multiChoiceConfig.style || 'Chip'}
                        layoutDirection={multiChoiceConfig.layoutDirection || 'Horizontal'}
                        showImage={multiChoiceConfig.showImage || false}
                        imageDisplay={multiChoiceConfig.imageDisplay || 'Fit'}
                        chipLabelPosition={multiChoiceConfig.chipLabelPosition || 'Below Image'}
                        captionAlignment={multiChoiceConfig.captionAlignment || 'Centre'}
                        chipSize={multiChoiceConfig.chipSize || 'Medium'}
                        layoutStyle={multiChoiceConfig.layoutStyle || 'Standard'}
                    />
                );
                
            default:
                return (
                    <TextField 
                        {...commonFieldProps}
                        title={fieldName}
                        exclusionType={fieldConfig.exclusionType || 'hide'}
                    />
                );
        }
    };

    const renderItem = (item: any, index: number) => {
        const isMultichoiceHorizontal = item.type === 'field' &&
            item.fieldType === 'multichoice' &&
            (item.configuration?.layoutDirection === 'Horizontal' ||
             item.configuration?.layoutDirection === 'horizontal');

        const flexClass = layout === 'horizontal' && item.type !== 'widget'
            ? (isMultichoiceHorizontal ? 'tw-flex-1 tw-min-w-0' : 'tw-flex-1')
            : 'tw-w-full';

        if (item.type === 'field') {
            const isFieldSelected = effectiveSelectedFieldId === item.id;
            const fieldWrapperId = `${baseId}-field-wrapper-${index}`;


            return (
                <div
                    key={`field-${item.id}-${index}`}
                    id={fieldWrapperId}
                    data-testid={fieldWrapperId}
                    className={`tw-component-wrapper tw-relative ${flexClass}`}
                    style={{
                        boxShadow: isFieldSelected ? 'inset 0 0 0 2px #1F7AE4' : 'none'
                    }}
                    onClick={(e) => handleFieldClick(e, item)}
                    data-field-id={item.id}
                    data-field-type={item.fieldType}
                >
                    {renderFieldItem(item, isFieldSelected)}
                </div>
            );
        }

        if (item.type === 'widget') {
            const isWidgetSelected = effectiveSelectedWidgetId === item.id;
            const widgetWrapperId = `${baseId}-widget-wrapper-${index}`;

            return (
                <div
                    key={`widget-${item.id}-${index}`}
                    id={widgetWrapperId}
                    data-testid={widgetWrapperId}
                    className="tw-relative tw-w-full"
                    style={{
                        boxShadow: isWidgetSelected ? 'inset 0 0 0 2px #1F7AE4' : 'none'
                    }}
                    onClick={(e) => handleWidgetClick(e, item)}
                    data-component-id={item.id}
                    data-component-type="widget"
                    data-widget-type={item.widgetType}
                >
                    {renderWidget(item)}
                </div>
            );
        }
        
        if (item.type === 'category') {
            const isFieldSelected = effectiveSelectedFieldId === item.id;
            // Use a unique key that includes configuration changes to force re-render
            const categoryKey = `category-${item.id}-${JSON.stringify(item.configuration).substring(0, 50)}`;
            const categoryWrapperId = `${baseId}-category-wrapper-${index}`;

            return (
                <div
                    key={categoryKey}
                    id={categoryWrapperId}
                    data-testid={categoryWrapperId}
                    className={`tw-relative ${flexClass}`}
                    style={{
                        boxShadow: isFieldSelected ? 'inset 0 0 0 2px #1F7AE4' : 'none'
                    }}
                    onClick={(e) => handleFieldClick(e, item)}
                    data-component-id={item.id}
                    data-component-type="category"
                >
                    <CategoryComponent
                        id={`${baseId}-category-${index}`}
                        {...buildCategoryProps({
                            ...item.configuration,
                            id: item.configuration?.id || item.id,
                            categoryId: item.configuration?.categoryId || item.dataConfiguration?.categoryId || item.id,
                            isEmpty: item.configuration?.isEmpty ?? item.dataConfiguration?.isEmpty ?? false,
                            actualCategoryId: item.configuration?.actualCategoryId || item.configuration?.categoryId || item.id
                        })}
                        isSelected={isFieldSelected}
                    />
                </div>
            );
        }

        if (item.type === 'connector') {
            const isFieldSelected = effectiveSelectedFieldId === item.id;
            const connectorWrapperId = `${baseId}-connector-wrapper-${index}`;
            return (
                <div
                    key={`connector-${item.id}-${index}`}
                    id={connectorWrapperId}
                    data-testid={connectorWrapperId}
                    className={`tw-relative ${flexClass}`}
                    style={{
                        boxShadow: isFieldSelected ? 'inset 0 0 0 2px #1F7AE4' : 'none'
                    }}
                    onClick={(e) => handleFieldClick(e, item)}
                    data-component-id={item.id}
                    data-component-type="connector"
                >
                    {renderFieldItem(item, isFieldSelected)}
                </div>
            );
        }

        if (item.nestedContainers) {
            const nestedWrapperId = `${baseId}-nested-placeholder-${index}`;
            return (
                <div key={`nested-containers-${index}`} id={nestedWrapperId} className="tw-mt-4">
                    <div id={`${nestedWrapperId}-message`} className="tw-text-sm tw-text-gray-500 tw-p-2 tw-border tw-border-dashed tw-border-gray-300 tw-rounded">
                        Nested containers would render here
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div id={baseId} data-testid={baseId} className="tw-w-full">
            {submodelInfo && submodel && (
                <div
                    id={`${baseId}-submodel`}
                    data-testid={`${baseId}-submodel`}
                    className={`tw-w-full tw-p-3 tw-cursor-pointer tw-overflow-visible ${
                        submodelHighlightLevel === 'primary'
                            ? 'tw-bg-blue-50 tw-border-2 tw-border-blue-500'
                            : submodelHighlightLevel === 'secondary'
                            ? 'tw-border tw-border-blue-300'
                            : submodelHighlightLevel === 'tertiary'
                            ? 'tw-bg-gray-50 tw-border tw-border-blue-200'
                            : 'hover:tw-border-gray-200'
                        }`}
                    onClick={handleSubmodelClick}
                    data-submodel-id={submodel.id}
                >
                    {!isNested && (
                        <div id={`${baseId}-submodel-header`} className="tw-flex tw-items-center tw-gap-2 tw-pb-3">
                            <SubModelIcon />
                            <div id={`${baseId}-submodel-info`}>
                                <div id={`${baseId}-submodel-name`} className="tw-text-base tw-font-semibold tw-text-[#172B4D]">
                                    {submodel.configuration?.showTitle !== false ? (
                                        submodel.configuration?.displayName || submodel.name
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    <div id={`${baseId}-submodel-items`} className={`${getLayoutClasses()} tw-overflow-visible`}>
                        {items.map((item, index) => renderItem(item, index))}
                    </div>
                </div>
            )}

            {!submodel && items && items.length > 0 && (
                <div id={`${baseId}-items`} className={`${getLayoutClasses()} tw-overflow-visible`}>
                    {items.map((item, index) => renderItem(item, index))}
                </div>
            )}

            {(!items || items.length === 0) && !submodel && (
                <div id={`${baseId}-empty-container`} className="tw-flex tw-items-center tw-justify-center tw-h-16 tw-bg-gray-50 tw-rounded-md tw-mt-2">
                    <span id={`${baseId}-empty-container-message`} className="tw-text-sm tw-text-gray-500">Empty container</span>
                </div>
            )}
        </div>
    );
};

export default ContainerContent;
